import { useState } from 'react';
import {
  useMutation, UseMutationResult, useQuery, UseQueryResult,
} from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import {
  deleteAccount,
  getAccountByUserEmail,
  getAccountPlanDefinitions,
  getAccounts,
  getAccountsInfo,
  getActiveAccountsWithFeatures,
} from '../../api/accountsApi';
import PaginationResponseDto from '../../interfaces/paginationResponse.interface';
import { ActiveFilters } from '../useTablePageParams';
import { ACCOUNTS_PAGE_FILTERS } from '../../pages/AccountsPage/accountsPageFilters';
import { TABLE } from '../../consts';
import { ACCOUNT_PARAMS } from '../../consts/requestParamsKeys';
import {
  Account,
  AccountDeleteRequest,
  AccountsResponse,
  AccountWithFeatures,
  GetAccountPlanDefinitionsRequest,
  PlanDefinition,
} from '../../@types/Account';

// todo: restore when server support
// const querySearchInColumns = [
//   ACCOUNT_PARAMS.DISPLAY_NAME,
//   ACCOUNT_PARAMS.ACCOUNT_ID,
//   ACCOUNT_PARAMS.SUBDOMAIN,
//   ACCOUNT_PARAMS.ASSOCIATED_COMPANY,
// ];

type useAccountsParams = {
  key: string;
  limit: number;
  select: string[];
  exact?: {
    [key: string]: string;
  };
  not?: {
    [key: string]: string;
  };
};

type useAccountsResponse = {
  accounts: Account[] | [];
  setSearchTerm: (value: string) => void;
  searchTerm: string;
  isLoading: boolean;
};

type GetAccountsResult = {
  error: unknown;
  data: AccountsResponse;
  isLoading: boolean;
  refetch: () => void;
};

type UseGetActiveAccountsResult = {
  activeAccounts: AccountsResponse;
  totalCount?: number;
  isLoading: boolean;
  refetch: () => void;
};

const useAccounts = ({
  key,
  limit,
  select,
  exact = {},
  not = {},
}: useAccountsParams): useAccountsResponse => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useQuery<PaginationResponseDto<Account>>(
    [key, searchTerm],
    () => getAccounts({
      select,
      exact,
      not,
      contains: {
        displayName: searchTerm,
      },
      limit,
    }),
  );

  return {
    accounts: data?.results || [],
    isLoading,
    searchTerm,
    setSearchTerm: (value) => setSearchTerm(value),
  };
};

const useGetAccountsInfo = (
  activeFilters: ActiveFilters,
  currentPage: number,
  activeSort: Sort,
): GetAccountsResult => {
  // const searchColumns = `{${querySearchInColumns.join(',')}}`;
  const searchQuery = activeFilters[ACCOUNTS_PAGE_FILTERS.QUERY]?.[0];

  const {
    error, data, isLoading, refetch,
  } = useQuery<
    PaginationResponseDto<Account>
  >(
    ['accounts', currentPage, activeFilters, activeSort],
    () => getAccountsInfo({
      page: currentPage,
      limit: TABLE.ROWS_PER_PAGE,
      contains: {
        [ACCOUNT_PARAMS.DISPLAY_NAME]: searchQuery,
      },
      in: {
        [ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS]:
            activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS],
        [ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN]:
            activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN],
        [ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN]:
            activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN],
      },
      order: activeSort,
    }),
    {
      keepPreviousData: true,
    },
  );

  return {
    error,
    data,
    isLoading,
    refetch,
  };
};

export const useGetAccountByUserEmail = (
  email: string,
): UseQueryResult<Account, unknown> => useQuery<Account>(['accounts', email], () => getAccountByUserEmail(email), {
  keepPreviousData: true,
  enabled: false, // disables automatic query on mount
  retry: false,
});

const useGetActiveAccountsWithFeatures = (
  activeFilters: ActiveFilters,
  currentPage: number,
  activeSort: Sort,
): UseGetActiveAccountsResult => {
  // const searchColumns = `{${querySearchInColumns.join(',')}}`;
  const searchQuery = activeFilters[ACCOUNTS_PAGE_FILTERS.QUERY]?.[0];

  const { data, isFetching, refetch } = useQuery<
    PaginationResponseDto<AccountWithFeatures>
  >(
    ['accounts', currentPage, activeFilters, activeSort],
    () => getActiveAccountsWithFeatures({
      page: currentPage,
      limit: TABLE.ROWS_PER_PAGE,
      contains: {
        [ACCOUNT_PARAMS.DISPLAY_NAME]: searchQuery,
      },
      in: {
        [ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN]:
            activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN],
        [ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN]:
            activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN],
      },
      order: activeSort,
    }),
    {
      keepPreviousData: true,
    },
  );

  return {
    activeAccounts: data?.results || [],
    totalCount: data?.totalCount,
    isLoading: isFetching,
    refetch,
  };
};

const useDeleteAccount = (
  onSuccessCallback: () => void,
): UseMutationResult<unknown, AxiosResponse, AccountDeleteRequest, unknown> => {
  return useMutation(
    (params) => {
      return deleteAccount(params);
    },
    {
      onSuccess: () => {
        onSuccessCallback();
      },
    },
  );
};

const useGetAccountPlanDefinitions = (payload?: GetAccountPlanDefinitionsRequest): UseQueryResult<
  PlanDefinition[],
  AxiosError
> => {
  return useQuery<PlanDefinition[], AxiosError | null>(
    ['account-definitions', payload?.recruitingUsageType],
    () => {
      return getAccountPlanDefinitions(payload);
    },
  );
};

export {
  useAccounts,
  useGetAccountsInfo,
  useDeleteAccount,
  useGetAccountPlanDefinitions,
  useGetActiveAccountsWithFeatures,
};
